import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { movies, movieCategories } from '@/mocks/movies';
import MovieCard from '@/components/feature/MovieCard';
import FilterBar, { categoryOptions } from './components/FilterBar';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 6;

function inDecade(year: number, decade: string) {
  if (decade === 'all') return true;
  const [start, end] = decade.split('-').map(Number);
  return year >= start && year <= end;
}

export default function Category() {
  const [params, setParams] = useSearchParams();
  const cat = params.get('cat') || 'all';
  const genre = params.get('genre') || '全部题材';
  const decade = params.get('decade') || 'all';
  const sort = params.get('sort') || 'newest';

  const [visible, setVisible] = useState(PAGE_SIZE);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const currentCat = movieCategories.find((c) => c.key === cat);
  const catLabel = categoryOptions.find((c) => c.key === cat)?.label || '影视分类';

  usePageMeta({
    title: `${catLabel} - 韩国伦理影视推荐`,
    description: `${catLabel}页面汇集优质韩国伦理影视作品，${currentCat ? `${currentCat.desc}` : '涵盖传统伦理剧、现代伦理剧与伦理电影，支持按题材、年代与热度筛选。'}`,
    keywords: `韩国伦理,${catLabel},韩国伦理影视,伦理影视推荐,韩剧伦理`,
  });

  const filtered = useMemo(() => {
    const list = movies.filter((m) => {
      if (cat !== 'all' && m.category !== cat) return false;
      if (genre !== '全部题材' && m.genre !== genre) return false;
      if (!inDecade(m.year, decade)) return false;
      return true;
    });
    if (sort === 'hot') list.sort((a, b) => b.views - a.views);
    else if (sort === 'rating') list.sort((a, b) => b.rating - a.rating);
    else list.sort((a, b) => b.year - a.year);
    return list;
  }, [cat, genre, decade, sort]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [cat, genre, decade, sort]);

  const setParam = (key: string, value: string, defaultValue: string) => {
    const next = new URLSearchParams(params);
    if (value === defaultValue) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const reset = () => setParams(new URLSearchParams(), { replace: true });

  const shown = filtered.slice(0, visible);

  return (
    <div className="pt-16">
      {/* Header */}
      <div className="w-full px-4 md:px-8 py-8 md:py-12 bg-primary-950">
        <div className="max-w-7xl mx-auto">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-primary-300/80 mb-4" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/category" className="hover:text-accent-400 transition-colors">影视分类</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">{catLabel}</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50">{catLabel}</h1>
          <p className="mt-3 text-sm md:text-base text-primary-200/85 max-w-3xl leading-relaxed">
            {currentCat
              ? currentCat.desc
              : '汇集本站收录的全部韩国伦理影视作品，可通过题材、年代与排序方式自由筛选，找到合你口味的内容。'}
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8">
          {/* Sidebar filters (desktop) */}
          <aside className="hidden lg:block w-[260px] shrink-0">
            <div className="sticky top-24 p-5 rounded-lg bg-background-50 border border-background-200">
              <FilterBar
                cat={cat}
                genre={genre}
                decade={decade}
                sort={sort}
                onCat={(v) => setParam('cat', v, 'all')}
                onGenre={(v) => setParam('genre', v, '全部题材')}
                onDecade={(v) => setParam('decade', v, 'all')}
                onSort={(v) => setParam('sort', v, 'newest')}
                onReset={reset}
              />
            </div>
          </aside>

          {/* Main */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-4 mb-5">
              <p className="text-sm text-foreground-600">
                共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 部作品
              </p>
              <button
                type="button"
                onClick={() => setMobileFilterOpen((v) => !v)}
                className="lg:hidden flex items-center gap-1 px-3 py-2 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-700 hover:bg-secondary-100 transition-colors"
              >
                <i className="ri-filter-3-line" /> 筛选
              </button>
            </div>

            {/* Mobile filter panel */}
            {mobileFilterOpen && (
              <div className="lg:hidden mb-6 p-5 rounded-lg bg-background-50 border border-background-200">
                <FilterBar
                  cat={cat}
                  genre={genre}
                  decade={decade}
                  sort={sort}
                  onCat={(v) => setParam('cat', v, 'all')}
                  onGenre={(v) => setParam('genre', v, '全部题材')}
                  onDecade={(v) => setParam('decade', v, 'all')}
                  onSort={(v) => setParam('sort', v, 'newest')}
                  onReset={reset}
                />
              </div>
            )}

            {shown.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">
                  {shown.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
                {visible < filtered.length && (
                  <div className="mt-8 text-center">
                    <button
                      type="button"
                      onClick={() => setVisible((v) => v + PAGE_SIZE)}
                      className="px-8 py-3 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      加载更多（剩余 {filtered.length - visible} 部）
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="py-20 text-center rounded-lg bg-background-100 border border-background-200">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-500 text-2xl">
                  <i className="ri-emotion-unhappy-line" />
                </div>
                <p className="mt-4 text-foreground-700">没有找到符合条件的影视作品</p>
                <p className="mt-2 text-sm text-foreground-500">试试调整筛选条件，或查看全部作品。</p>
                <div className="mt-6 flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={reset}
                    className="px-6 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors whitespace-nowrap"
                  >
                    重置筛选条件
                  </button>
                  <Link
                    to="/"
                    className="px-6 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm hover:bg-background-200 transition-colors whitespace-nowrap"
                  >
                    返回首页
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}